import React from "react"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgCheck } from "@digits-shared/components/SVGIcons/line/Check.svg"
import { SvgChevronDown } from "@digits-shared/components/SVGIcons/line/ChevronDown.svg"
import { SvgChevronUp } from "@digits-shared/components/SVGIcons/line/ChevronUp.svg"
import { SvgInfoCircle } from "@digits-shared/components/SVGIcons/line/InfoCircle.svg"
import { SvgXClose } from "@digits-shared/components/SVGIcons/line/XClose.svg"
import { DigitsButton } from "@digits-shared/DesignSystem/Button"
import {
  ClearButtonWrapper,
  COMMON_SELECT_ITEM_STYLES,
  COMMON_SELECT_TRIGGER_STYLES,
  SELECT_CHEVRON_WRAPPER_STYLES,
  SELECT_TRIGGER_CLASSNAME,
  SelectInputContainer,
  SelectLabel,
  SelectWrapper,
} from "@digits-shared/DesignSystem/FormElements/Selects/common"
import { DigitsTooltip } from "@digits-shared/DesignSystem/Tooltip"
import useStateBoolean from "@digits-shared/hooks/useStateBoolean"
import colors from "@digits-shared/themes/colors"
import * as SelectPrimitive from "@radix-ui/react-select"
import { type SelectItemProps, type SelectProps } from "@radix-ui/react-select"
import styled, { css } from "styled-components"

const LabelGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const InfoIcon = styled(SvgInfoCircle)`
  ${svgPathStyles(colors.secondary, 2.1)};
  width: 12px;
  height: 12px;
  border-radius: 50%;
`

const TooltipButton = styled.button`
  display: flex;
  background: ${colors.transparent};
  border: none;
  padding: 0;
  outline: none;

  &:focus-visible {
    ${InfoIcon} {
      outline: 1px auto ${colors.accentBlue};
    }
  }
`

const SelectTrigger = styled(SelectPrimitive.Trigger)<{ $invalid?: boolean }>`
  ${COMMON_SELECT_TRIGGER_STYLES};
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

export const IconWrapper = styled.div<{ $open: boolean }>`
  ${SELECT_CHEVRON_WRAPPER_STYLES};
  flex-shrink: 0;
`

const ScrollButtonStyles = css`
  height: 24px;
  background: ${colors.white};
  width: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
    ${svgPathStyles(colors.secondary50, 1.5)};
  }
`

const ScrollUpButton = styled(SelectPrimitive.ScrollUpButton)`
  ${ScrollButtonStyles};
`

const ScrollDownButton = styled(SelectPrimitive.ScrollDownButton)`
  ${ScrollButtonStyles};
`

const StyledPrimitiveItem = styled(SelectPrimitive.Item)`
  ${COMMON_SELECT_ITEM_STYLES};
  min-width: var(--radix-select-trigger-width);
  max-height: var(--radix-select-content-available-height);

  &[aria-selected="true"] {
    background: ${colors.glowHighlight};
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

const ActiveIndicator = styled(SelectPrimitive.ItemIndicator)`
  position: absolute;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  right: 8px;
  width: 16px;
  height: 16px;
  ${svgPathStyles(colors.accentPurple, 3)};
`

const SelectContent = styled(SelectPrimitive.Content)<{ $small: boolean }>`
  background: ${colors.white};
  box-shadow: 0 0 12px 0 ${colors.translucentSecondary20};
  border-radius: 8px;
  z-index: 10;
  max-height: 300px;
  overflow-y: auto;

  ${({ $small }) =>
    $small
      ? css`
          ${StyledPrimitiveItem} {
            height: 32px;
            padding: 0 24px 0 8px;
          }
        `
      : css`
          ${StyledPrimitiveItem} {
            height: 40px;
            padding: 0 24px 0 12px;
          }
        `};
`

export type SelectValue = { label: string; value: string }

export const Select = React.forwardRef<
  HTMLButtonElement,
  React.PropsWithChildren<
    SelectProps & {
      id?: string
      small?: boolean
      placeholder?: string
      label?: string
      invalid?: boolean
      fullWidth?: boolean
      onCloseAutoFocus?: ((event: Event) => void) | undefined
      className?: string
      allowClear?: boolean
      tooltip?: React.ReactNode
      values?: SelectValue[] | string[]
      openOnFocus?: boolean
      closeOnTab?: boolean
      CustomDropdownIcon?: ({ $open }: { $open: boolean }) => React.ReactNode
    }
  >
>(({ children, ...props }, forwardedRef) => {
  const {
    small = false,
    fullWidth,
    placeholder,
    id,
    label,
    invalid,
    className,
    allowClear = false,
    value: valueProvided,
    onValueChange: onValueChangeProvided,
    onOpenChange: onOpenChangeProvided,
    open: openProvided,
    onCloseAutoFocus,
    values,
    tooltip,
    openOnFocus,
    closeOnTab,
    CustomDropdownIcon,
    ...rest
  } = props

  const [tooltipOpen, setTooltipOpen] = React.useState(false)
  const toggleTooltip = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setTooltipOpen((current) => !current)
  }, [])
  const [monitoredValue, setMonitoredValue] = React.useState<string>(
    props.defaultValue || valueProvided || ""
  )
  const [monitoredOpen, setMonitoredOpen] = React.useState<boolean>(
    typeof openProvided === "boolean" ? openProvided : false
  )

  const { value: didBlur, setTrue: setDidBlur } = useStateBoolean()
  const mergedValue = typeof valueProvided === "undefined" ? monitoredValue : valueProvided
  const mergedOpen = typeof openProvided === "undefined" ? monitoredOpen : openProvided
  const showClear = allowClear && mergedValue && !mergedOpen && !props.disabled

  const onValueChangeComposed = React.useCallback(
    (value: string) => {
      setMonitoredValue(value)
      onValueChangeProvided?.(value)
    },
    [onValueChangeProvided]
  )
  const isClosing = React.useRef(false)
  const onOpenChangeComposed = React.useCallback(
    (open: boolean) => {
      setMonitoredOpen(open)
      onOpenChangeProvided?.(open)
      if (!open) {
        setDidBlur()
        isClosing.current = true
      }
    },
    [onOpenChangeProvided, setDidBlur]
  )

  const onFocus = React.useCallback(
    (
      e:
        | React.FocusEvent<HTMLButtonElement, Element>
        | React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
      // Only open if focused via keyboard and not closing
      if ("detail" in e && e.detail === 0 && !isClosing.current && openOnFocus) {
        onOpenChangeComposed(true)
      }
      isClosing.current = false
    },
    [onOpenChangeComposed, openOnFocus]
  )

  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Tab" && mergedOpen && closeOnTab) {
        e.preventDefault()
        onOpenChangeComposed(false)
      }
    },
    [mergedOpen, closeOnTab, onOpenChangeComposed]
  )
  const clearSelect = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onValueChangeComposed("")
    },
    [onValueChangeComposed]
  )

  const isInvalid = invalid || (props.required && !mergedValue && didBlur)

  return (
    <SelectInputContainer
      $small={small}
      fullWidth={fullWidth}
      className={className}
      aria-label={label}
    >
      {label && (
        <LabelGroup>
          <SelectLabel htmlFor={id} $invalid={invalid} aria-label={label}>
            {label}
          </SelectLabel>
          {tooltip && (
            <DigitsTooltip
              content={tooltip}
              side="top"
              delayDuration={250}
              open={tooltipOpen}
              onOpenChange={setTooltipOpen}
              openOnFocus
            >
              <TooltipButton
                type="button"
                role="tooltip"
                aria-label={tooltip.toString()}
                onClick={toggleTooltip}
              >
                <InfoIcon aria-hidden />
              </TooltipButton>
            </DigitsTooltip>
          )}
        </LabelGroup>
      )}
      <SelectWrapper>
        <SelectPrimitive.Root
          open={mergedOpen}
          value={mergedValue}
          onValueChange={onValueChangeComposed}
          onOpenChange={onOpenChangeComposed}
          {...rest}
        >
          <SelectTrigger
            id={id}
            onBlur={setDidBlur}
            onFocus={onFocus}
            ref={forwardedRef}
            $invalid={isInvalid}
            aria-invalid={isInvalid}
            aria-label={label}
            key="trigger"
            // tab index must be explicitly set for focus to work in Safari
            tabIndex={0}
            className={SELECT_TRIGGER_CLASSNAME}
          >
            <SelectPrimitive.Value placeholder={placeholder} />
            {!showClear &&
              !props.disabled &&
              ((CustomDropdownIcon && <CustomDropdownIcon $open={mergedOpen} />) || (
                <IconWrapper aria-hidden $open={mergedOpen}>
                  <SvgChevronDown />
                </IconWrapper>
              ))}
          </SelectTrigger>
          <SelectPrimitive.Portal key="portal">
            <SelectContent
              $small={small}
              align="start"
              position="popper"
              side="bottom"
              sideOffset={4}
              onCloseAutoFocus={onCloseAutoFocus}
            >
              <ScrollUpButton>
                <SvgChevronUp />
              </ScrollUpButton>
              <SelectPrimitive.Viewport>
                {values
                  ? values.map((item) =>
                      typeof item === "string" ? (
                        <SelectItem
                          key={item}
                          value={item}
                          onKeyDown={onKeyDown}
                          showActiveIndicator={false}
                          aria-selected={mergedValue === item}
                          children={item}
                        />
                      ) : (
                        <SelectItem
                          key={item.value}
                          value={item.value}
                          onKeyDown={onKeyDown}
                          aria-selected={mergedValue === item.value}
                          showActiveIndicator={false}
                          children={item.label}
                        />
                      )
                    )
                  : children}
              </SelectPrimitive.Viewport>
              <ScrollDownButton>
                <SvgChevronDown />
              </ScrollDownButton>
            </SelectContent>
          </SelectPrimitive.Portal>
        </SelectPrimitive.Root>
        {showClear && (
          <ClearButtonWrapper key="clear" $small={small}>
            <DigitsButton
              type="button"
              $circle
              $variant="ghost-dark"
              onClick={clearSelect}
              size={small ? "small" : "medium"}
              aria-label="clear"
            >
              <SvgXClose />
            </DigitsButton>
          </ClearButtonWrapper>
        )}
      </SelectWrapper>
    </SelectInputContainer>
  )
})

export const SelectItem = React.forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<SelectItemProps & { showActiveIndicator?: boolean }>
>(({ children, showActiveIndicator = true, ...props }, forwardedRef) => (
  <StyledPrimitiveItem {...props} ref={forwardedRef}>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    {showActiveIndicator && (
      <ActiveIndicator asChild>
        <SvgCheck />
      </ActiveIndicator>
    )}
  </StyledPrimitiveItem>
))
